import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ChartBarIcon, 
  ArrowTrendingUpIcon, 
  ArrowTrendingDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import Card from '../../components/common/Card'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import { Recommendation } from '../../types'
import { recommendationsApi } from '../../services/api'

const RecommendationsPage: React.FC = () => {
  const navigate = useNavigate()
  const [recommendations, setRecommendations] = useState<Recommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<'ALL' | 'BUY' | 'SELL' | 'HOLD'>('ALL')
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'ACTIVE' | 'COMPLETED' | 'STOPPED'>('ALL')

  useEffect(() => {
    fetchRecommendations()
  }, [])

  const fetchRecommendations = async () => {
    try {
      setLoading(true)
      const data = await recommendationsApi.getRecommendations()
      setRecommendations(data || [])
    } catch (error) {
      console.error('Error fetching recommendations:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredRecommendations = recommendations.filter(rec => {
    const matchesSearch = rec.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rec.company_name.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'ALL' || rec.recommendation_type === filterType
    const matchesStatus = filterStatus === 'ALL' || rec.status === filterStatus
    
    return matchesSearch && matchesType && matchesStatus
  })

  const getPerformanceColor = (performance: number | undefined) => {
    if (!performance) return 'text-gray-400'
    return performance > 0 ? 'text-green-600' : 'text-red-600'
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-blue-100 text-blue-800'
      case 'COMPLETED':
        return 'bg-green-100 text-green-800'
      case 'STOPPED':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-dark-700 text-gray-300'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-100">Recommendations</h1>
          <p className="text-gray-300">Track all your investment recommendations and their performance</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Button>
            <ChartBarIcon className="w-4 h-4 mr-2" />
            View Performance Report
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by symbol or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-3 py-2 bg-dark-800 border border-dark-600 text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Types</option>
            <option value="BUY">Buy</option>
            <option value="SELL">Sell</option>
            <option value="HOLD">Hold</option>
          </select>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-3 py-2 bg-dark-800 border border-dark-600 text-gray-100 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="COMPLETED">Completed</option>
            <option value="STOPPED">Stopped</option>
          </select>
          
          <Button variant="outline">
            <FunnelIcon className="w-4 h-4 mr-2" />
            More Filters
          </Button>
        </div>
      </Card>

      {/* Recommendations List */}
      <div className="space-y-4">
        {filteredRecommendations.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <ChartBarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-100 mb-2">
                {recommendations.length === 0 ? "No recommendations available" : "No recommendations found"}
              </h3>
              <p className="text-gray-300">
                {recommendations.length === 0 
                  ? "We currently don't have any published recommendations."
                  : "Try adjusting your search or filter criteria"}
              </p>
            </div>
          </Card>
        ) : (
          filteredRecommendations.map((rec) => (
            <Card key={rec.id}>
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                {/* Left Section */}
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-3">
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                      rec.recommendation_type === 'BUY' 
                        ? 'bg-green-100 text-green-800' 
                        : rec.recommendation_type === 'SELL'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {rec.recommendation_type}
                    </div>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(rec.status)}`}>
                      {rec.status}
                    </div>
                    {rec.is_premium && (
                      <div className="px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded-full">
                        Premium
                      </div>
                    )}
                  </div>
                  
                                    <h3 className="text-lg font-semibold text-gray-100 mb-1">
                    {rec.company_name}
                  </h3>
                  <p className="text-gray-300 mb-3">{rec.title}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-400 block">Entry Price</span>
                      <span className="font-medium">₹{rec.entry_price}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">Current Price</span>
                      <span className="font-medium">₹{rec.current_price}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">Target Price</span>
                      <span className="font-medium">₹{rec.target_price}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">Stop Loss</span>
                      <span className="font-medium">₹{rec.stop_loss}</span>
                    </div>
                    <div>
                      <span className="text-gray-400 block">Performance</span>
                      <div className={`flex items-center font-medium ${getPerformanceColor(rec.performance)}`}>
                        {rec.performance && rec.performance > 0 ? (
                          <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
                        ) : (
                          <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
                        )}
                        {rec.performance?.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Section */}
                <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col items-end">
                  <div className="text-right mb-3">
                    <div className="text-sm text-gray-400">
                      Published: {new Date(rec.published_at || rec.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => navigate(`/dashboard/recommendations/${rec.slug || rec.id}`)}
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    View Details
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}

export default RecommendationsPage
